import React, { useEffect, useState } from "react";
import { deletePipe } from "../../APICalls/DeleteRequests";
import { fetchData } from "../../APICalls/GetRequests";
import { changePipeName } from "../../APICalls/PutRequests";
import Cookies from "js-cookie";
import {
  postPipePercentage,
  postPipeState,
  fetchSinglePipe,
} from "../../APICalls/GetRequests";
import "./SinglePipePopup.scss";
import Spinner from "../Spinner/Spinner";

const SinglePipePopup = ({
  field,
  pipes,
  pipeId,
  handlePipePopupClose,
  setPipes,
  refreshPipes,
}) => {
  const [selectedPipe, setSelectedPipe] = useState(null);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [newPipeName, setNewPipeName] = useState("");
  const [leftOpenPercent, setLeftOpenPercent] = useState(null);
  const [rightOpenPercent, setRightOpenPercent] = useState(null);
  const [serverLeftOpenPercent, setServerLeftOpenPercent] = useState(null);
  const [serverRightOpenPercent, setServerRightOpenPercent] = useState(null);
  const [pipeState, setPipeState] = useState({
    left_gate_open: false,
    right_gate_open: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const pipe = pipes.find((pipe) => pipe.id === pipeId);
    if (pipe) {
      setSelectedPipe(pipe);
      setNewPipeName(pipe.name);
    }
  }, [pipes, pipeId]);

  useEffect(() => {
    if (!selectedPipe) return;

    setLoading(true);
    fetchSinglePipe(selectedPipe.id, Cookies.get("session_key"))
      .then((data) => {
        const leftGate = data.gates.find((g) => g.local_id === 0);
        const rightGate = data.gates.find((g) => g.local_id === 1);

        setPipeState({
          left_gate_open: leftGate?.open || false,
          right_gate_open: rightGate?.open || false,
        });
        setServerLeftOpenPercent(leftGate?.percent ?? null);
        setServerRightOpenPercent(rightGate?.percent ?? null);
        setSelectedPipe(data); // Refresh full pipe data
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  }, [selectedPipe?.id]);

  const toggleRenameForm = () => {
    setShowRenameForm(!showRenameForm);
  };

  const handleInputChange = (e) => {
    setNewPipeName(e.target.value);
  };

  const updatePipeName = () => {
    setLoading(true);
    changePipeName(pipeId, {name: newPipeName }, Cookies.get("session_key")
    )
      .then(() => {
        refreshPipes();
        setShowRenameForm(false);
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  const handleLeftOpenPercentChange = (e) => {
    const value = Number(e.target.value);
    setLeftOpenPercent(value > 0 && value <= 100 ? value : null);
  };

  const handleRightOpenPercentChange = (e) => {
    const value = Number(e.target.value);
    setRightOpenPercent(value > 0 && value <= 100 ? value : null);
  };

  const updateLeftOpenPercent = () => {
    if (leftOpenPercent === null || !selectedPipe) return;

    setLoading(true);
    fetchSinglePipe(selectedPipe.id, Cookies.get("session_key"))
      .then((data) => {
        const gate = data.gates.find((g) => g.local_id === 0);
        if (!gate) return;

        return postPipePercentage(
          gate.id,
          leftOpenPercent,
          Cookies.get("session_key")
        );
      })
      .then(() => {
        fetchAndUpdatePipeState();
        setLeftOpenPercent(null);
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  const updateRightOpenPercent = () => {
    if (rightOpenPercent === null || !selectedPipe) return;

    setLoading(true);
    fetchSinglePipe(selectedPipe.id, Cookies.get("session_key"))
      .then((data) => {
        const gate = data.gates.find((g) => g.local_id === 1);
        if (!gate) return;

        return postPipePercentage(
          gate.id,
          rightOpenPercent,
          Cookies.get("session_key")
        );
      })
      .then(() => {
        fetchAndUpdatePipeState();
        setRightOpenPercent(null);
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  const fetchAndUpdatePipeState = () => {
    setLoading(true);
    fetchSinglePipe(selectedPipe.id, Cookies.get("session_key"))
      .then((data) => {
        const leftGate = data.gates.find((g) => g.local_id === 0);
        const rightGate = data.gates.find((g) => g.local_id === 1);

        setPipeState({
          left_gate_open: leftGate?.open || false,
          right_gate_open: rightGate?.open || false,
        });
        setServerLeftOpenPercent(leftGate?.percent ?? null);
        setServerRightOpenPercent(rightGate?.percent ?? null);
        setSelectedPipe(data);
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  const toggleGate = (side) => {
    if (!selectedPipe) return;

    const localId = side === "left" ? 0 : 1;

    setLoading(true);
    fetchSinglePipe(selectedPipe.id, Cookies.get("session_key"))
      .then((data) => {
        const gate = data.gates.find((g) => g.local_id === localId);
        if (!gate) throw new Error(`${side} gate not found`);

        return postPipeState(gate.id, Cookies.get("session_key"));
      })
      .then(() => {
        fetchAndUpdatePipeState();
        refreshPipes();
      })
      .catch((err) => {
        console.error(err);
        fetchAndUpdatePipeState();
        refreshPipes();
      })
      .finally(() => setLoading(false));
  };

  const removePipe = () => {
    if (!selectedPipe) return;

    setLoading(true);
    deletePipe({ id: selectedPipe.id }, Cookies.get("session_key"))
      .then(() => {
        fetchData("allPipes").then((data) => {
          handlePipePopupClose();
          setPipes(data);
        });
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  if (!selectedPipe) return null;

  return (
    <div className="popup-container">
      <div className="popup">
        <h1 className="pipe-name">Pipe: {selectedPipe.name}</h1>
        {showRenameForm ? (
          <div className="rename-form">
            <input
              type="text"
              value={newPipeName}
              onChange={handleInputChange}
              placeholder="Enter new pipe name"
              className="rename-input"
              disabled={loading}
            />
            <button
              onClick={updatePipeName}
              className="update-button"
              disabled={loading}
            >
              Update Pipe Name
            </button>
          </div>
        ) : (
          <button
            onClick={toggleRenameForm}
            className="rename-button"
            disabled={loading}
          >
            Rename Pipe
          </button>
        )}

        <div className="left-right-section-container">
          <div className="left-section">
            <h2 className="percentage-title">
              Left Open Percent: {serverLeftOpenPercent}
            </h2>
            <h3 className="update-title">Update Left Open Percentage</h3>
            <input
              type="number"
              value={leftOpenPercent === null ? "" : leftOpenPercent}
              onChange={handleLeftOpenPercentChange}
              className="percentage-input"
              disabled={loading}
              min="1"
              max="100"
            />
            <button
              onClick={updateLeftOpenPercent}
              className="update-button"
              disabled={loading || leftOpenPercent === null}
            >
              Update Left
            </button>
            <div className="gate-toggle">
              <h3 className="toggle-title">Toggle Left Gate</h3>
              <button
                onClick={() => toggleGate("left")}
                className={`toggle-button ${
                  pipeState.left_gate_open ? "close-gate" : "open-gate"
                }`}
                disabled={loading}
              >
                {loading ? (
                  <Spinner
                    color="white"
                    backgroundColor={
                      pipeState.left_gate_open ? "#0096c7" : "red"
                    }
                  />
                ) : pipeState.left_gate_open ? (
                  "Close Left Gate"
                ) : (
                  "Open Left Gate"
                )}
              </button>
            </div>
          </div>

          <div className="right-section">
            <h2 className="percentage-title">
              Right Open Percent: {serverRightOpenPercent}
            </h2>
            <h3 className="update-title">Update Right Open Percentage</h3>
            <input
              type="number"
              value={rightOpenPercent === null ? "" : rightOpenPercent}
              onChange={handleRightOpenPercentChange}
              className="percentage-input"
              disabled={loading}
              min="1"
              max="100"
            />
            <button
              onClick={updateRightOpenPercent}
              className="update-button"
              disabled={loading || rightOpenPercent === null}
            >
              Update Right
            </button>
            <div className="gate-toggle">
              <h3 className="toggle-title">Toggle Right Gate</h3>
              <button
                onClick={() => toggleGate("right")}
                className={`toggle-button ${
                  pipeState.right_gate_open ? "close-gate" : "open-gate"
                }`}
                disabled={loading}
              >
                {loading ? (
                  <Spinner
                    color="white"
                    backgroundColor={
                      pipeState.right_gate_open ? "#0096c7" : "red"
                    }
                  />
                ) : pipeState.right_gate_open ? (
                  "Close Right Gate"
                ) : (
                  "Open Right Gate"
                )}
              </button>
            </div>
          </div>
        </div>

        <button onClick={handlePipePopupClose} className="close-button">
          Close
        </button>
      </div>
    </div>
  );
};

export default SinglePipePopup;
